import * as _ from 'lodash'
import { v4 } from 'uuid'
import React, { useEffect, useMemo, useReducer } from 'react';
import componentServices from './componentServices';
import {  IPatchOperation, IShapeContext, ShapeFilter } from './modelFactory';
import util from './util';
import * as actions from './actions';
import validator from './validator';
import { Button } from './components/sharedComponents';
import { IAlert } from './models/alert';

const enum SaveState {
	Clean,
	Dirty,
	Saving
}
interface IPersistable {
	getPatch: (target: object, keys?: string[], context?: IShapeContext) => IPatchOperation[];
	toDocument: (keys?: string[], filter?: string[] | ShapeFilter, context?: IShapeContext) => Record<string, any>;
}
interface ISaveRootConfig<TModel extends IPersistable> { 
	autoSave: boolean;
	globalSave: boolean;
	warnOnLeave: boolean;
	stateId: string;
	save(doc: TModel, patch: IPatchOperation[]): Promise<any>;
	savedCallback?: ({autoSaving, preSave, error }: { autoSaving: boolean, preSave: boolean, error?: any }) => void;
	prepForSave?: (model: TModel, patch:IPatchOperation[]) => void;
	processAlert?: (alert: IAlert) => IAlert;
	validate?: (model: TModel) => string[];
	modelFactory: (data: TModel | Record<string, any>) => TModel;
	resolveMutableModel: (current: TModel | null) => [ model: TModel, discardChanges?: boolean ];
}
interface IAutoSaveState<TModel extends IPersistable> {
	mutableModel: TModel | null;
	pendingPatch: IPatchOperation[];
	lastAlert: IAlert | null;
	diffBase: TModel | null;
	saveState: SaveState;
	stateId: string;
}

function SaveButton({ initialLabel, saveHandler, getSaveState }: { initialLabel: string, saveHandler: () => void, getSaveState: () => SaveState}){
	function onClick (e) {
		e.preventDefault();
		saveHandler();
	}

	const saveState = getSaveState();
	const label = saveState === SaveState.Clean ? initialLabel : saveState === SaveState.Dirty ? 'Save' : 'Saving'; // eslint-disable-line no-nested-ternary
	const classes = ['action', 'save'].concat(saveState === SaveState.Clean ? 'disabled' : undefined);

	return (
		<Button className={classes.join(' ')} onClick={onClick} label={label} processing={ saveState === SaveState.Saving } />
	);
}

function AutoSaveNavWarning({ callback, message, saveState }: { callback:(proceed: boolean) => void, message: string, saveState: SaveState }) {
	function doCallback (proceed: boolean) {
		actions.popModal();

		setTimeout(() => {
			callback(proceed);
		}, 0);
	}

	function onClick (e, val) {
		e.preventDefault();
		doCallback(val);
	}
	
	const buttons = [];
	let title;

	switch (saveState) {
	case 1:
		buttons.push(<button key="cancel" onClick={ e => onClick(e, false) }><span>Stay on this form</span></button>);
		buttons.push(<button key="proceed" onClick={ e => onClick(e, true) }><span>Discard my changes</span></button>);
		title = 'Invalid Data';
		break;
	case 2:
		buttons.push(<button key="cancel" onClick={ e => onClick(e, false) }><span>Stay on this form</span></button>);
		buttons.push(<button key="proceed" onClick={ e => onClick(e, true) }><span>Continue anyway</span></button>);
		title = 'Save in Progress';
		break;
	default:
		// navigate away if the save completes before the modal is closed
		doCallback(true);
		return null;
	}

	return (
		<div className="alert error">
			<section>
				<header><span>{ title }</span></header>
				<div className="content">
					{ message }
					<div className="actions">
						{ buttons }
					</div>
				</div>
			</section>
		</div>
	);
}

let _debounceSaveTimer;
function clearDebouncedSave () {
	if (_debounceSaveTimer) {
		clearTimeout(_debounceSaveTimer);
		_debounceSaveTimer = null;
	}
}

const _globalButtonStack = [];

function setGlobalButton(button) {
	actions.setHeaderAction({
		key: 'global-autosave',
		element: button
	});
}

function autoSaveReducer<TModel extends IPersistable>(state: IAutoSaveState<TModel>, action: { type: 'change' | 'save-begin' | 'save-end', changes?: Partial<IAutoSaveState<TModel>> }) {
	switch(action.type) {
	case 'change':
		return { ...state, ...action.changes };
	case 'save-begin':
		return state;
	case 'save-end':
		return state;
	}
}

const _autoSaveStates: Record<string, IAutoSaveState<IPersistable>> = {};
function getAutoSaveState<TModel extends IPersistable>(stateId: string) {
	return _autoSaveStates[stateId] as IAutoSaveState<TModel>
}

export function useSaveRoot<TModel extends IPersistable>(config: ISaveRootConfig<TModel>, initialModel?: TModel) {
	const [state, dispatch] = useReducer(autoSaveReducer, {
		mutableModel: initialModel? config.modelFactory(initialModel.toDocument()): null,
		pendingPatch: [],
		lastAlert: null,
		diffBase: initialModel || null,
		saveState: SaveState.Clean,
		stateId: config.stateId
	});

	// console.log(`autosave state count ${Object.keys(_autoSaveStates).length}`);

	const { stateId } = state;

	_autoSaveStates[stateId] = state; 

	const helper = useMemo(() => {
		// console.log(`creating helper for ${stateId}`);
		const helperInstance = {
			saving: false,
			getCurrentState() {
				return getAutoSaveState<TModel>(stateId);
			},
			getSaveState () {
				if (config.globalSave) {
					return componentServices.store.getIn(['viewState', 'globalSave', 'state']);
				}
				else {
					const currentState = helperInstance.getCurrentState();
		
					return currentState?.saveState ?? SaveState.Clean;
				}
			},
			setSaveState (newState: SaveState) {
				const currentState = helperInstance.getCurrentState();
				
				if (config.globalSave) {
					actions.setGlobalSaveState({ state: newState });
				}
				else {
					const hasChanged = currentState?.saveState !== newState;
		
					if(hasChanged) {
						dispatch({ type: 'change', changes: { saveState: newState }});
						// setLocalSaveState(newState);
					}
				}
			},
			getRouteLeaveMessage () {
				const currentState = helperInstance.getCurrentState();
				if(currentState) {
					let resolvedSaveState = helperInstance.getSaveState();
			
					if (helperInstance.saving || currentState.pendingPatch.length) {
						resolvedSaveState = SaveState.Dirty;
						helperInstance.innerSave({ model: currentState.mutableModel, autoSaving: true });
					}
			
					switch (resolvedSaveState) {
					case 1:
						if (config.validate) {
							const messages = config.validate(currentState.mutableModel as TModel);
			
							if (messages.length) {
								return 'There is some missing or invalid information on this form that will need to be corrected before saving. Leaving this form now will discard your changes.';
							}
						}
						else {
							return 'You have unsaved changes. Leaving this form now will discard your changes.';
						}
						break;
					case 2:
						return 'Your changes are still being saved. Please wait until the save completes to be sure you don\'t lose any changes.';
					default:
						break;
					}
				}
		
				return undefined;
			},
			confirmLeave (callback, message = '') { // callback signature is: proceed => {}
				message = message || helperInstance.getRouteLeaveMessage();
				const wrappedCallback = proceed => {
					if (proceed) {
						try {
							const { diffBase } = helperInstance.getCurrentState();
							const mutableModel = config.modelFactory(diffBase.toDocument());

							helperInstance.setSaveState(SaveState.Clean);
							dispatch({ type: 'change', changes: { mutableModel, pendingPatch: [] } });
							clearDebouncedSave();
							componentServices.router.setRouteLeaveConfirmation(null, null);
						}
						catch (err) {
							// swallow the error
						}
					}
					callback(proceed);
				};
		
				if (message) {
					actions.pushModal({ component: <AutoSaveNavWarning message={message} callback={wrappedCallback} saveState={ helperInstance.getSaveState() } /> });
				}
				else {
					callback(true);
				}
			},
			registerRouteLeaveConfirmation () {
				componentServices.router.setRouteLeaveConfirmation(() => {
					return helperInstance.getRouteLeaveMessage();
				}, (message, callback) => {
					helperInstance.confirmLeave(callback, message);
				});
			},
			setSaveAlert (err) {
				const currentState = helperInstance.getCurrentState();
				if (currentState.lastAlert) {
					actions.clearAlert(currentState.lastAlert);
					dispatch({ type: 'change', changes: { lastAlert: null }});
				}
		
				if (err) {
					let alert: IAlert = {
						key: util.generateGuid(),
						type: 'error',
						message: err.message,
						isCloseable: true
					};
		
					if (config.processAlert) {
						alert = config.processAlert(alert);
					}
		
					if (alert) {
						// lastAlert = alert;
						actions.pushAlert(alert);
						dispatch({ type: 'change', changes: { lastAlert: alert }});
					}
				}
			},
			getSaveButton (initialLabel = 'Saved') {
				const saveHandler = () => {
					// this ensures that the bound saveHandler executes against the most current state rather than the closure state
					const { diffBase, mutableModel } = helperInstance.getCurrentState();
		
					if(mutableModel) {
						helperInstance.innerSave({ model: mutableModel, patch: mutableModel.getPatch(diffBase), autoSaving: false });
					}
				};
		
				return <SaveButton initialLabel={ initialLabel } getSaveState={ helperInstance.getSaveState } saveHandler={ saveHandler } />;
			},
			async innerSave ({ model, autoSaving, patch, messages, callback }: { model: TModel, autoSaving: boolean, patch?: IPatchOperation[], messages?: string[], callback?: any}) { 
				const currentState = helperInstance.getCurrentState();
				const { pendingPatch } = currentState;

				if (helperInstance.saving || _debounceSaveTimer) {
					return;
				}
		
				const savePatch = util.mergePatch(pendingPatch.concat(), patch);
		
				const doCallback = (err) => {
					if (callback) {
						try {
							callback(err);
						}
						catch (errCb) { /* swallow */ }
					}
				};
		
				const notifyCaller = (err?, preSave?) => {
					helperInstance.saving = false;
					// this ensures that the bound saveHandler executes against the most current state rather than the closure state
					const currentState = helperInstance.getCurrentState();
					if (err) {
						if (!preSave) {
							// TODO: eval if we need to reset the diffBase here as well
							const mergedPatch = util.mergePatch(savePatch, currentState.pendingPatch);
		
							dispatch({ type: 'change', changes: { pendingPatch: mergedPatch }});
							helperInstance.setSaveState(mergedPatch.length? SaveState.Dirty: SaveState.Clean);
						}
					}
					else if (autoSaving && currentState.pendingPatch.length) {
						helperInstance.innerSave({ model: currentState.mutableModel, autoSaving: autoSaving, patch: currentState.pendingPatch });
					}
					else {
						helperInstance.setSaveState(SaveState.Clean);
					}
		
					try {
						if (!preSave) { doCallback(err); }
						if (config.savedCallback) {
							config.savedCallback({ autoSaving: !!autoSaving, preSave: !!preSave, error: err });
						}
					}
					catch (errCb) {
						// swallow this
					}
				};
		
				const handleError = (err, preSave?) => {
					helperInstance.setSaveState(SaveState.Dirty);
					if (!autoSaving) {
						helperInstance.setSaveAlert(err);
					}
					notifyCaller(err, preSave);
				};
		
				try {
					messages = messages || config.validate(model);
		
					if (messages.length) {
						validator.throwValidationError(messages.join('\n'));
					}
		
					const nextSaveState = (!savePatch || savePatch.length === 0) ? SaveState.Clean : SaveState.Saving;
		
					if (nextSaveState === SaveState.Clean) { return; }
		
					helperInstance.saving = true;
					helperInstance.setSaveAlert(undefined);
		
					helperInstance.setSaveState(nextSaveState);
		
					// this allows the model owner one last chance to mutate the model or patch before save
					if (config.prepForSave) {
						config.prepForSave(model, savePatch);
					}
		
					// only clear the pending patch once we know we will save
					dispatch({ type: 'change', changes: { pendingPatch: [] }});
		
					const snapshot = model.toDocument();
					// diffBase = config.modelFactory(snapshot);
					dispatch({ type: 'change', changes: { diffBase: config.modelFactory(snapshot) }});
		
					try {
						await config.save(snapshot as TModel, savePatch);
						notifyCaller();
					} catch (saveErr) {
						handleError(saveErr);
					}
				}
				catch (err) {
					handleError(err, true);
				}
			},
			update (updater: (model: TModel) => void, options: { autoSave?: boolean }) {
				const currentState = helperInstance.getCurrentState();
				const { diffBase, mutableModel } = currentState;

				if(!mutableModel) {
					console.warn('Cannot update without a mutableModel');
					return;
				}
		
				const opts = _.defaults(options || {}, { autoSave: false });
		
				if (config.autoSave === false) {
					opts.autoSave = false;
				}
				// const nextState = _.assign({}, previousState); // ensure a re-render
				const currentSaveState = helperInstance.getSaveState();
		
				updater(mutableModel);
		
				let updatePatch = mutableModel.getPatch(diffBase);
		
				// an extraneousPatch allows an update to modify models that it does not own
				if (opts.extraneousPatch) {
					updatePatch = _.concat(updatePatch, opts.extraneousPatch);
				}
		
				if (updatePatch && updatePatch.length) {
					if (opts.autoSave) {
						clearDebouncedSave();
		
						// WARNING: debounced saves may lose extraneous patches. do not use debouncing with extraneous patches
						if (opts.debounceTimeout) {
							const debouncedPatch = [].concat(updatePatch);
							_debounceSaveTimer = setTimeout(() => {
								clearDebouncedSave();
								helperInstance.innerSave({ model: mutableModel, autoSaving: true, patch: debouncedPatch, callback: opts.callback });
							}, opts.debounceTimeout);
		
							helperInstance.setSaveState(SaveState.Dirty);
						}
						else {
							const messages = config.validate? config.validate(mutableModel): [];
							helperInstance.innerSave({ model: mutableModel, autoSaving: opts.autoSave, patch: messages.length === 0 ? updatePatch : undefined, messages, callback: opts.callback });
						}
					}
					else if (currentSaveState !== 2) {
						// console.log('activePatch', activePatch);
						// just ensure that we trigger a re-render
						dispatch({ type: 'change', changes: { pendingPatch: currentState.pendingPatch.concat() }});
						helperInstance.setSaveState(SaveState.Dirty);
					}
				}
				else {
					// pendingPatch = [];
					dispatch({ type: 'change', changes: { pendingPatch: [] }});
					if (currentSaveState !== 2) {
						helperInstance.setSaveState(SaveState.Clean);
					}
				}
			}
		}

		return helperInstance;
	}, [stateId]);

	// manage the global button and _autoSaveStates
	useEffect(() => {
		helper.setSaveState(0);
		if (config.globalSave) { 
			if (config.warnOnLeave) {
				helper.registerRouteLeaveConfirmation();
			}

			const button = helper.getSaveButton();
			_globalButtonStack.push({ button, stateId });
			setGlobalButton(button);
		}

		return () => {
			delete _autoSaveStates[stateId];
			if (config.globalSave) { 
				_globalButtonStack.pop();
	
				actions.deleteHeaderAction({ key: 'global-autosave' });
				if (_globalButtonStack.length) {
					const top = _globalButtonStack[_globalButtonStack.length - 1];
					const saveState = getAutoSaveState(top.stateId);
					
					setGlobalButton(top.button);
					if(saveState) {
						helper.setSaveState(saveState.saveState);
					}
				}
			}
		};
	}, []);

	// initialize or rebase the mutable model
	useEffect(() => {
		const currentState = helper.getCurrentState();

		if(!currentState) {
			return;
		}

		const { mutableModel, pendingPatch } = currentState;
		const [resolvedMutable, discardChanges = false] = config.resolveMutableModel(mutableModel);

		if(resolvedMutable && resolvedMutable !== mutableModel) {
			if(mutableModel === null) { // formerly 'init'
				dispatch({ type: 'change', changes: { pendingPatch: [], diffBase: config.modelFactory(resolvedMutable.toDocument()) }});
			}
			else { // formerly 'rebase'
				const rebasePatch = discardChanges? []: pendingPatch;

				// be sure all future diffing is performed against this new base
				const diffBase = config.modelFactory(resolvedMutable.toDocument());

				helper.setSaveState(rebasePatch.length? SaveState.Dirty: SaveState.Clean);
				dispatch({ type: 'change', changes: { diffBase, pendingPatch: rebasePatch} });				
			}

			dispatch({ type: 'change', changes: { mutableModel: resolvedMutable}});
		}
	})

	const autoSave = {
		confirmLeave: helper.confirmLeave,
		update: helper.update,
		getSaveButton: helper.getSaveButton,
		saveState: helper.getSaveState(),
		model: helper.getCurrentState().mutableModel,
	}
	return [autoSave];
}